<template>
	<widget title="Personnel Roles">

		<onboarding-card v-if="!isWizardInstance" title="Set up custom personnel roles" name="Personnel Roles" setting-key="personnelRoles">
			<p>You can create custom personnel roles and assign to help better organize specific tasks for different people on a project. We have added a few commonly used roles to get you started.</p>
		</onboarding-card>

		<table-list
			:records="mutatedRoles"
			:fields="fields"
			record-name="Personnel Role"
			:has-edit="true"
			id-field="slug"
			:loading="rolesLoading"
			v-on:edit="editRole">

			<button class="btn btn-success" v-on:click="newRole">
				<i class="fa fa-plus"></i>
				Add New Role
			</button>
		</table-list>

		<table-list
			:records="mutatedTechnicians"
			:fields="technicianFields"
			record-name="Technician"
			has-edit="/employee/{?}/profile"
			id-field="slug"
			:loading="techsLoading">

			<h3>Active Service Crew Members</h3>
		</table-list>

		<modal-dialog
			:modal-visible="editModal.visible"
			:title="editModal.title"
			confirm-button-text="Save"
			v-on:confirm="save"
			v-on:closeModal="editModal.visible = false">

			<field label="Title" 
				col-class="col-sm-3" 
				v-model="editModal.payload.title"
				v-on:change="titleChange">		
			</field>

			<field label="Title Abbreviation" 
				col-class="col-sm-4" 
				v-model="editModal.payload.abbreviation"
				:params="{type:'text',maxlength: 3}">		
			</field>

			<field label="Display this role on Project/Service Listings" 
				col-class="col-8 col-sm-8 col-md-9" 
				v-model="editModal.payload.display_on_listings" 
				:params="{type: 'boolean'}" 
				:float-control-right="true">
			</field>

			<field label="This role is in charge of management of the job" 
				col-class="col-8 col-sm-8 col-md-9" 
				v-model="editModal.payload.is_management_lead" 
				:params="{type: 'boolean'}" 
				:float-control-right="true">
			</field>

			<field label="This role is in charge of field operations of the job" 
				col-class="col-8 col-sm-8 col-md-9" 
				v-model="editModal.payload.is_field_ops_lead" 
				:params="{type: 'boolean'}" 
				:float-control-right="true">
			</field>

			<h4>This Role should:</h4>

			<field label="Be required on Projects" 
				col-class="col-8 col-sm-8 col-md-9" 
				v-model="editModal.payload.role_required" 
				:params="{type: 'boolean'}" 
				:float-control-right="true">
			</field>

			<field label="Be required on Service Jobs" 
				col-class="col-8 col-sm-8 col-md-9" 
				v-model="editModal.payload.role_required_service" 
				:params="{type: 'boolean'}" 
				:float-control-right="true">
			</field>

			<field label="Receive notifications for incomplete company forms" 
				col-class="col-8 col-sm-8 col-md-9" 
				v-model="editModal.payload.company_form_notices" 
				:params="{type: 'boolean'}" 
				:float-control-right="true">
			</field>

			<h4>Permissions on Projects/Services Assigned To</h4>

			<field label="Read" 
				col-class="col-8 col-sm-8 col-md-9" 
				v-model="editModal.payload.read_perms" 
				:params="{type: 'boolean'}" 
				:float-control-right="true">
			</field>

			<field label="Write" 
				col-class="col-8 col-sm-8 col-md-9" 
				v-model="editModal.payload.write_perms" 
				:params="{type: 'boolean'}" 
				:float-control-right="true">
			</field>

			<field label="See Revenue & Expenses Widget" 
				col-class="col-8 col-sm-8 col-md-9" 
				v-model="editModal.payload.read_financials" 
				:params="{type: 'boolean'}" 
				:float-control-right="true">
			</field>

			<field label="Write Quotes/Invoices" 
				col-class="col-8 col-sm-8 col-md-9" 
				v-model="editModal.payload.write_invoices" 
				:params="{type: 'boolean'}" 
				:float-control-right="true">
			</field>
			<p class="text-muted">This will allow roles who have this permission to create/update/delete invoices on the project or service.</p>

			<field label="Write Timelogs" 
				col-class="col-8 col-sm-8 col-md-9" 
				v-model="editModal.payload.write_timelogs" 
				:params="{type: 'boolean'}" 
				:float-control-right="true">
			</field>
			<p class="text-muted">This will allow roles who have this permission to manipulate time logs attached to the project, for example clock user's in/out.</p>

			<field label="Reviews Time Logs" 
				col-class="col-8 col-sm-8 col-md-9" 
				v-model="editModal.payload.review_timelogs" 
				:params="{type: 'boolean'}" 
				:float-control-right="true">
			</field>

			<field label="Reviews Expenses" 
				col-class="col-8 col-sm-8 col-md-9" 
				v-model="editModal.payload.review_expenses" 
				:params="{type: 'boolean'}" 
				:float-control-right="true">
			</field>

			<field label="Can see open message threads" 
				col-class="col-8 col-sm-8 col-md-9" 
				v-model="editModal.payload.see_open_threads" 
				:params="{type: 'boolean'}" 
				:float-control-right="true">
			</field>

			<h4>Role Adjustments</h4>

			<p>If an employee is assigned this role on a project, they get a pay increase of:</p>

			<field label="Amount" 
				col-class="col-sm-6 col-md-6" 
				v-model="editModal.payload.payboost_amount" 
				:params="{type: 'number', step: 0.01, min: 0}">
			</field>

			<field label="Type" 
				col-class="col-sm-6 col-md-6" 
				:params="{type:'btn-group',buttons:boostTypeBtns}" 
				v-model="editModal.payload.payboost_is_percent">
			</field>

			<field label="Except for (Classification Exclusions)" 
				col-class="col-sm-6 col-md-6" 
				:params="{type:'select-multi',options:classificationsOptions}" 
				v-model="editModal.payload.EmployeeClassification">
			</field>

			<div class="row" v-if="editModal.showDelete">
				<div class="col-sm-3" style="padding-top: 3px;">

					<button type="button" v-if="editModal.record.User && editModal.record.User.length > 0" class="btn btn-outline-danger float-left" disabled><i class="fa fa-trash"></i> Delete</button>

					<button type="button" v-else class="btn btn-outline-danger float-left" v-on:click="deleteItem"><i class="fa fa-trash"></i> Delete</button>
					
				</div>
				<div v-if="editModal.record.User && editModal.record.User.length > 0" class="col-sm-9 text-muted">
					Personnel roles may only be deleted if no employee has been assigned to it.
				</div>
			</div>
			
		</modal-dialog>

	</widget>
</template>

<script>
import Widget from "@/components/Widget"
import TableList from "@/components/TableList"
import ModalDialog from "@/components/ModalDialog"
import Field from "@/components/Field"

import OnboardingMixins from "@/mixins/OnboardingMixins"
import OnboardingCard from "@/components/OnboardingCard"

export default {

	name: 'PersonnelRolesWidget',
	components: {
		TableList,
		ModalDialog,
		Field,
		Widget,
		OnboardingCard
	},
	mixins: [OnboardingMixins],
	props: ["isWizardInstance"],
	data() {
		return {
			roles: [],
			rolesLoading: false,
			technicians: [],
			techsLoading: false,
			fields: [
				{
					label: "Title",
					key: "titleWithAbbreviation"
				},
				{
					label: "Read Job",
					key: "read_perms",
					type: "boolean",
					align:"center"
				},
				{
					label: "Write Job",
					key: "write_perms",
					type: "boolean",
					align:"center"
				},
				{
					label: "See Revenue & Expenses Widget",
					key: "read_financials",
					type: "boolean",
					align:"center"
				},
				{
					label: "Write Quotes /Invoices",
					key: "write_invoices",
					type: "boolean",
					align:"center"
				},
				{
					label: "Write Time Logs",
					key: "write_timelogs",
					type: "boolean",
					align:"center"
				},
				{
					label: "Reviews Time Logs",
					key: "review_timelogs",
					type: "boolean",
					align:"center"
				},
				{
					label: "Reviews Expenses",
					key: "review_expenses",
					type: "boolean",
					align:"center"
				},
				{
					label: "See Messages",
					key: "see_open_threads",
					type: "boolean",
					align:"center"
				},
				{
					label: "Display on Listings",
					key: "display_on_listings",
					type: "boolean",
					align:"center"
				},
				{
					label: "Company Form Notices",
					key: "company_form_notices",
					type: "boolean",
					align:"center"
				},
				{
					label: "Managment Lead",
					key: "is_management_lead",
					type: "boolean",
					align:"center"
				},
				{
					label: "Field Ops Lead",
					key: "is_field_ops_lead",
					type: "boolean",
					align:"center"
				},
			],
			technicianFields: [
				{label: "Name",key:"fullName"},
				{label: "Explicitly Assigned as a Service Crew Member",key:"explicitTechnician",type:"boolean"},
				{label: "Assigned to Services",key:"autoTechnician",type:"boolean"}
			],
			editModal: {
				visible: false,
				title: "Edit Role",
				payload: {
					title: "",
					write_perms: null,
					read_perms: null,
					review_timelogs: null
				},
				record: {},
				showDelete: true
			},
			boostTypeBtns: [
				{
					icon: "fa-percent",
					text: " of Regular Rate",
					value: 1
				},
				{
					icon: "fa-money-bill",
					text: "/hr",
					value: 0
				},
			],
		}
	},
	computed: {
		mutatedRoles() {
			if (!this.roles)
				return []
			
			return this.roles.map(role => {
				role.titleWithAbbreviation = role.title

				if (role.abbreviation)
					role.titleWithAbbreviation += " ("+role.abbreviation+")"

				return role
			})
		},
		mutatedTechnicians() {
			return this.technicians.map(tech => {
				tech.fullName = tech.first_name+" "+tech.last_name

				tech.explicitTechnician = false
				tech.autoTechnician = false

				if ("EmployeeSetting" in tech && tech.EmployeeSetting.length > 0 && tech.EmployeeSetting[0].is_service_tech == 1)
					tech.explicitTechnician = true

				if ("Service" in tech && tech.Service && tech.Service.length > 0)
					tech.autoTechnician = true

				return tech
			})
		},
		classificationsOptions() {
			return this.optionify(this.$store.employeeClassifications,"title");
		}
	},
	methods: {
		titleChange(title) {

			if (!this.editModal.payload.abbreviation) {
				let abbr = ""

				//auto-generate abbreviation
				let words = title.split(" ")

				//if multiple words, make an acronym
				if (words.length > 1) {
					words.forEach(word => {
						abbr += word.substring(0,1)
					})
					abbr = abbr.toUpperCase().substring(0,3)
				}
				else 
					//shorten to first two letters
					abbr = title.substring(0,2)
				
				this.editModal.payload.abbreviation = abbr
			}
		},
		newRole() {
			this.editModal.payload = {
				title: "",
				abbreviation: "",
				write_perms: false,
				read_perms: false,
				review_timelogs: false
			}
			this.editModal.visible = true
			this.editModal.title = "New Project Personnel Role"
			this.editModal.record = {}
			this.editModal.showDelete = false
		},
		editRole(record) {
			this.editModal.payload = record

			this.editModal.payload.EmployeeClassification = this.primitize(this.editModal.payload.EmployeeClassification,"id")

			this.editModal.visible = true
			this.editModal.title = "Edit Project Personnel Role"
			this.editModal.record = record
			this.editModal.showDelete = true
		},
		save() {
			let vm = this
			this.saveRecord("JobPersonnelRole",this.editModal.payload,function(){
				vm.editModal.visible = false
				vm.alertSaved("Personnel Role saved!")
				vm.fetchData()
			})

		},
		deleteItem() {
			let vm = this

			let payload = {
				id: this.editModal.payload.id
			}

			this.postData("JobPersonnelRole/delete",payload).then(response => {
				vm.fetchData()
				vm.editModal.visible = false
			})
		},
		fetchServiceTechs() {
			const vm = this

			this.techsLoading = true

			const autoTechParams = {
				//filters: [["Service.assigned_to", this.$store.userDetails.id]],  optimize query by excluding sub-modal filter
				include: [
					{
						model: "Service",  // this may take a while...
						fields: ["id","slug"],
						filters: [['assigned_to', this.$store.userDetails.id]],
						limit:1
					}
				],
				fields: ["id","slug","first_name","last_name"]
			}

			const userParams = {
				filters: [
					["EmployeeSetting.is_service_tech",1],
				],
				include: [{
					model: "EmployeeSetting",
					fields: ["id","slug","is_service_tech"]
				}],
				fields: ["id","slug","first_name","last_name","EmployeeSetting"]
			}

			const promises = [
				this.getJSON("User/get",autoTechParams),
				this.getJSON("User/get",userParams)
			]

			Promise.all(promises).then(responses => {

				const usersWithTechRole = responses[0].data.filter(user => {
					return ('Service' in user && user.Service && user.Service.length > 0)
				})

				let technicians = usersWithTechRole.concat(responses[1].data)

				//remove duplicate users
				technicians = technicians.filter((tech, index, self) => index === self.findIndex(t => tech && t.id === tech.id))

				//sort by name
				vm.technicians = technicians.sort((a,b) => {
					return (a.first_name.toUpperCase() < b.first_name.toUpperCase()) ? -1 : 1
				})

				vm.techsLoading = false
			})

		},
		fetchData() {
			let vm = this

			this.rolesLoading = true

			let promises = [
				this.getJSON("JobPersonnelRole/get",{include:[{model:"EmployeeClassification",fields:["id","slug","title"]}]}),
				this.fetchModel("EmployeeClassification"),
			]

			Promise.all(promises).then(responses => {
				vm.rolesLoading = false
				vm.roles = responses[0].data
			})
		}
	},
	created() {
		this.fetchData()
		this.fetchServiceTechs()
	}

}
</script>
